<template>
  <div
    v-if="lot && lot.state !== 'DRAFT'"
    class="card-tile flex flex-col w-full h-auto relative"
  >
    <div class="image-content">
      <div
        class="image h-full w-full flex justify-center items-center relative rounded-xl overflow-hidden"
      >
        <NuxtImg
          v-if="lot.images[0]"
          :src="lot.images[0].thumbSquare || '/jpg/catalog/empty-image.jpg'"
          :alt="lot.name"
          class="w-full object-cover max-w-[318px] md:aspect-[1.2] aspect-square rounded-xl overflow-hidden"
          loading="lazy"
          format="webp"
          quality="90"
        />

        <LazyLotsDisplayTypeAdultWrapper v-if="needBlur" />

        <LotsShowInsuranceBadge
          v-if="lot.category?.insurance"
          :show-text="showInsuranceText"
          class="absolute left-2 bottom-2 z-10 pointer-events-none"
        />

        <div
          v-if="$device.isDesktop && !needBlur"
          class="on-hover bg-gray-300 h-full w-full hidden"
        >
          <RmLink
            v-if="!isUnavailable"
            :to="generateLink(lot.id)"
            color="gray500"
            class="btn-hover"
            @click="rememberLotSource(lot.id)"
            @auxclick="rememberLotSource(lot.id)"
            @contextmenu="handleContextMenu(lot.id)"
          >
            {{ $t('card.viewBtn') }}
          </RmLink>
          <RmLink
            v-if="isCanEdit"
            :to="$localePath(`/lots/${lot.id}/edit`)"
            color="gray500"
            class="btn-hover"
          >
            {{ $t('card.editBtn') }}
          </RmLink>
        </div>

        <div
          v-if="isUnavailable"
          class="top-2 left-10 absolute z-10 flex items-center justify-center p-2 bg-white rounded-full"
        >
          <LazySharedRmTooltip
            :placement="'bottom'"
            :triggers="['hover', 'focus']"
            :distance="8"
          >
            <NuxtImg
              src="/svg/lot/eye-slash.png"
              alt="Лот скрыт"
              class="w-6 h-6"
              format="webp"
              quality="80"
            />
            <template #popper> Лот скрыт </template>
          </LazySharedRmTooltip>
        </div>
        <div
          v-else-if="isShowDoNotRemove && !lot.doNotRemove"
          class="top-2 left-10 absolute z-10 flex items-center justify-center px-2 py-1 text-xs text-white bg-red-500 rounded-full cursor-pointer"
        >
          <LazySharedRmTooltip
            :placement="'top'"
            :triggers="['click']"
            :distance="8"
          >
            <div> Лот скоро будет скрыт </div>
            <template #popper>
              Чтобы не скрывать лот, перейдите на страницу редактирования лота и
              нажмите “Не скрывать лот”.
            </template>
          </LazySharedRmTooltip>
        </div>
      </div>
    </div>

    <div class="text-content">
      <div class="m-0">
        <nuxt-link
          :to="generateLink(lot.id)"
          class="text-gray-600 text-xxs md:text-xs flex items-center gap-1 font-normal h-4"
          :aria-label="`Перейти к лоту ${lot.name} от ${lot.user?.publicName || 'пользователя'}`"
          @click="rememberLotSource(lot.id)"
          @auxclick="rememberLotSource(lot.id)"
          @contextmenu="handleContextMenu(lot.id)"
        >
          <span v-if="lot.user">
            {{ lot.user.publicName }}
          </span>
          <span v-if="lot.user && lot.user.avgRating">
            {{ lot.user.avgRating }}
          </span>

          <svg
            v-if="lot.user && lot.user.avgRating"
            width="14"
            height="14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14 5.5c0-.3-.5-.3-.7-.3H8.8L7.4 1c0-.2-.2-.5-.4-.5s-.4.3-.4.5L5.2 5.2H.6c-.1 0-.6 0-.6.3 0 .2.2.4.4.5L4 8.6l-1.5 4.2v.4c0 .2 0 .3.2.3l.5-.2L7 10.7l3.7 2.6.5.2c.2 0 .2-.2.2-.3v-.4L9.8 8.6 13.6 6c.2-.1.4-.3.4-.5Z"
              fill="#FC0"
            />
          </svg>
        </nuxt-link>
        <div
          class="name text-gray-900 font-semibold capitalize mb-[2px] h-auto leading-4 md:leading-5"
        >
          <nuxt-link
            :key="lot.id"
            :to="generateLink(lot.id)"
            :title="lot.name"
            class="text-xs md:text-base pt-1 block"
            @click="rememberLotSource(lot.id)"
            @auxclick="rememberLotSource(lot.id)"
            @contextmenu="handleContextMenu(lot.id)"
          >
            {{ lot.name }}
          </nuxt-link>
        </div>
        <div
          v-if="!isElka && hasCardPrices"
          class="flex flex-col items-start w-full gap-1"
        >
          <template
            v-for="(item, idx) in lot.cardPrices"
            :key="idx"
          >
            <span class="text-sm md:text-base font-extrabold whitespace-nowrap">
              <span class="text-gray-600">
                {{ item.period }}
                {{
                  item.kind === 'HOUR'
                    ? $t('common.hour', item.period)
                    : $t('common.day', item.period)
                }}&nbsp;-&nbsp;</span><span class="text-green-500">
                {{ item.price }}&nbsp;₽
              </span>
            </span>
          </template>
        </div>
        <div v-else-if="isElka">
          <span class="text-base font-extrabold text-green-500">
            {{ elkaPrice }}
          </span>
        </div>
        <span
          v-else
          class="text-sm md:text-base font-extrabold whitespace-nowrap text-gray-600"
        >
          {{ $t('lots.show.prices.onRequest') }}
        </span>
      </div>
    </div>
    <nuxt-link
      v-if="$device.isMobileOrTablet"
      :to="generateLink(lot.id)"
      class="card-link-mobile"
      :aria-label="`Перейти к лоту ${lot.name} от ${lot.user?.publicName || 'пользователя'}`"
      @click="rememberLotSource(lot.id)"
      @auxclick="rememberLotSource(lot.id)"
      @touchstart="handleTouchStart(lot.id)"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    />
  </div>
</template>

<script>
import LotsShowInsuranceBadge from '~/components/lots/show/insurance_badge.vue'
import RmLink from '~/components/shared/rm_link.vue'

export default {
  components: {
    LotsShowInsuranceBadge,
    RmLink,
  },
  props: {
    lot: {
      type: Object,
      default () {
        return {}
      },
    },
    isUnavailable: {
      type: Boolean,
      default: false,
    },
    isShowDoNotRemove: {
      type: Boolean,
      default: false,
    },
    pageName: {
      type: String,
      default: '',
    },
    pageType: {
      type: String,
      default: '',
    },
    sourceBlockType: {
      type: String,
      default: '',
    },
    showInsuranceText: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const searchForm = useSearchForm()
    return {
      isAdultConfirmed: useGlobal().isAdultConfirmed,
      currentCategoryId: computed(() => searchForm.category.value?.id),
      currentCategory: searchForm.category,
      currentUserId: useAuth().currentUserId,
    }
  },
  data () {
    return {
      longPressTimer: null,
    }
  },
  computed: {
    isCanEdit () {
      if (!this.lot.user?.id) {
        return false
      }
      return parseInt(this.currentUserId) === parseInt(this.lot.user.id)
    },
    isElka () {
      const config = useRuntimeConfig()
      return (
        config.public.elka.includes(Number(this.lot.id)) ||
          Number(this.lot.id) === Number(config.public.elka)
      )
    },
    elkaPrice () {
      const config = useRuntimeConfig()
      const lotId = Number(this.lot.id)
      const price = config.public.elkaPrices?.[lotId] || config.public.elkaPrices?.[45933]
      return `${price.toLocaleString(this.$i18n.locale)}\u00A0₽`
    },
    needBlur () {
      return this.lot.adultOnly && !this.isAdultConfirmed && !this.isCanEdit
    },
    hasCardPrices () {
      return this.lot.cardPrices?.length > 0
    },
  },
  methods: {
    rememberLotSource (lotId) {
      try {
        const key = `lot_source_context:${lotId}`
        const now = Date.now()
        const payload = {
          pc: this.currentCategoryId || '',
          spn: this.pageName || '',
          spt: this.pageType || '',
          sbt: this.sourceBlockType || '',
          ts: now,
          expires: now + 30 * 1000,
        }
        window.localStorage.setItem(key, JSON.stringify(payload))
      } catch {
        // ignore storage errors (private mode, quota, etc)
      }
    },
    generateLink (lotId) {
      return this.$localePath(`/lots/${lotId}`)
    },
    handleContextMenu (lotId) {
      this.rememberLotSource(lotId)
    },
    handleTouchStart (lotId) {
      this.longPressTimer = window.setTimeout(() => {
        this.handleContextMenu(lotId)
        this.longPressTimer = null
      }, 500)
    },
    handleTouchEnd () {
      if (this.longPressTimer) {
        window.clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    },
  },
}
</script>

<style lang="sass" scoped>
  .card-tile
    .image
      &:hover
        .on-hover
          background-color: rgba(60, 60, 60, 0.8)
          backdrop-filter: blur(2px)
          @apply flex flex-col justify-center items-center absolute

    .btn-hover, .btn-mobile
      @apply rounded-full font-bold text-sm my-2
      width: 130px
      height: 36px

    .btn-mobile
      font-size: 0.7rem

    .image-content
      @apply flex justify-center items-center mb-2 overflow-hidden relative
      width: 100%
      height: auto
    .text-content
      .name
        padding-left: 1px
        display: -webkit-box
        -webkit-line-clamp: 2
        -webkit-box-orient: vertical
        overflow: hidden
        width: 100%
    .card-link-mobile
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        z-index: 1

    .on-hover
      :deep(svg)
        path
          fill: #FFF
</style>
